import React, { useState, useEffect, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import AssamMap from "../AssamMap";
import {
  barLabelPlugin,
  barLabelOutsidePlugin,
  hBarOpts,
  sortBtn,
} from "../../utils/chartHelpers";
import CardHead from "../../utils/CardHead";
import {
  fetchDistrictAvailability,
  fetchFacilityAvailability,
  fetchFacilityTypeAvailability,
  fetchCategoryStock,
} from "../../data/api";

function cellBadge(val) {
  if (val === null)
    return <span style={{ color: "#94a3b8", fontSize: ".7rem" }}>—</span>;
  const bg = val >= 90 ? "#dcfce7" : val >= 75 ? "#fef3c7" : "#fee2e2";
  const cl = val >= 90 ? "#15803d" : val >= 75 ? "#b45309" : "#b91c1c";
  return (
    <span
      style={{
        background: bg, color: cl, fontWeight: 700, borderRadius: 4,
        padding: "3px 7px", display: "inline-block", minWidth: 44,
      }}
    >
      {val}%
    </span>
  );
}

export default function TabStockAvailability() {
  const [availSort, setAvailSort] = useState("desc");
  const [districts, setDistricts] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchDistrictAvailability()
      .then((r) => setDistricts(r.data))
      .catch(console.error);
    fetchFacilityAvailability()
      .then((r) => setFacilities(r.data))
      .catch(console.error);
    fetchCategoryStock()
      .then((r) => setCategories(r.data))
      .catch(console.error);
  }, []);

  const sortedDistricts = useMemo(
    () =>
      [...districts]
        .sort((a, b) =>
          availSort === "desc" ? b.avail - a.avail : a.avail - b.avail
        )
        .slice(0, 10),
    [availSort, districts]
  );

  const availChartData = {
    labels: sortedDistricts.map((d, i) => `#${i + 1}  ${d.n}`),
    datasets: [
      {
        label: "Stock Availability %",
        data: sortedDistricts.map((d) => parseFloat(d.avail)),
        backgroundColor: sortedDistricts.map((d) =>
          d.avail >= 90 ? "#34d399" : d.avail >= 80 ? "#86efac" : d.avail >= 70 ? "#fde68a" : "#fdba74"
        ),
        borderRadius: 5,
      },
    ],
  };

  const catChartData = useMemo(
    () => ({
      labels: categories.map((c) => c.cat),
      datasets: [
        {
          label: "Avg Months Stock",
          data: categories.map((c) => parseFloat(c.avg_months_stock) || 0),
          backgroundColor: categories.map((c) =>
            c.avg_months_stock >= 3 ? "#34d399" : c.avg_months_stock >= 1 ? "#fbbf24" : "#f87171"
          ),
          borderRadius: 4,
          barThickness: 30,
        },
      ],
    }),
    [categories]
  );

  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true },
      tooltip: { callbacks: { label: (ctx) => "  " + ctx.parsed.x + "%" } },
      datalabels: { display: false },
    },
    scales: {
      x: { min: 0, max: 100, ticks: { callback: (v) => v + "%" }, grid: { color: "#f1f5f9" } },
      y: { ticks: { font: { size: 10 } } },
    },
    animation: { duration: 400 },
  };

  const catOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      datalabels: { display: true, anchor: 'end', align: 'top', color: '#444', font: { weight: '700', size: 11 }, formatter: v => v.toFixed(1) },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 11, weight: '600' } } },
      y: { ticks: { font: { size: 10 }, callback: v => v + ' mo' }, grid: { color: "#f1f5f9" }, title: { display: true, text: 'Months of Stock', font: { size: 10 } } },
    },
  };

  // Build facility x category matrix from real district data
  const DRUG_CATS = ["EDL", "SDL"];
  const fcMatrix = useMemo(() => {
    return facilities.map((f) => ({
      fac: f.name,
      avail: f.avail,
      total: f.total,
    }));
  }, [facilities]);

  // District x category matrix
  const dcMatrix = useMemo(() => {
    return [...districts]
      .sort((a, b) => a.n.localeCompare(b.n))
      .map((d) => ({
        name: d.n,
        avail: parseFloat(d.avail),
        total_stores: d.total_stores,
        stores_above_90: d.stores_above_90,
        stockout_stores: d.stockout_stores,
      }));
  }, [districts]);

  return (
    <div className={`viz-pane active`}>
      <div className="row g-3">
        <div className="col-md-6 d-flex">
          <div className="card w-100 d-flex flex-column">
            <CardHead
              icon="fas fa-map-marked-alt"
              title="Assam District Health Map"
              subtitle="Stock availability by district · hover for details"
            />
            <div className="card-body flex-grow-1 p-2">
              <AssamMap mode="avail" />
            </div>
          </div>
        </div>
        <div className="col-md-6 d-flex">
          <div className="card w-100 d-flex flex-column">
            <CardHead
              icon="fas fa-chart-bar"
              title="District Ranking based on Availability"
              subtitle={
                <span className="d-flex align-items-center gap-1">
                  Top 10 |{" "}
                  <span>{availSort === "desc" ? "Descending" : "Ascending"}</span>
                  {sortBtn(availSort === "desc", () => setAvailSort("desc"), "▼")}
                  {sortBtn(availSort === "asc", () => setAvailSort("asc"), "▲")}
                </span>
              }
            />
            <div className="card-body flex-grow-1" style={{ minHeight: 300 }}>
              <Bar data={availChartData} options={options} plugins={[barLabelPlugin]} />
            </div>
          </div>
        </div>
      </div>

      {/* Product Category + Facility Availability */}
      <div className="row g-3 mt-1">
        <div className="col-md-6 d-flex">
          <div className="card w-100 d-flex flex-column">
            <CardHead
              icon="fas fa-boxes-stacked"
              title="Product Category Wise Stock (Months)"
              subtitle="Average months of stock by category"
            />
            <div className="card-body flex-grow-1">
              <div style={{ position: "relative", height: 300 }}>
                <Bar data={catChartData} options={catOptions} />
              </div>
              <div className="d-flex gap-3 mt-3 align-items-center">
                {[
                  ["#34d399", "≥ 3 months Good"],
                  ["#fbbf24", "1-3 months Warning"],
                  ["#f87171", "< 1 month Critical"],
                ].map(([bg, lbl], i) => (
                  <span key={i} className="d-flex align-items-center gap-1"
                    style={{ fontSize: ".68rem", color: "#374151", whiteSpace: "nowrap" }}>
                    <span style={{ display: "inline-block", width: 12, height: 12, borderRadius: 2, background: bg }}></span>
                    {lbl}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-6 d-flex">
          <div className="card w-100 d-flex flex-column">
            <CardHead
              icon="fas fa-hospital"
              title="Facility Type Wise Availability"
              subtitle="Average availability % by facility type"
            />
            <div className="card-body flex-grow-1">
              <div className="fac-list">
                {facilities.length > 0 ? facilities.map((f, i) => (
                  <div key={i} className="fac-row">
                    <div className="fac-name">{f.name} <span style={{ fontSize: '.6rem', color: 'var(--muted)' }}>({f.total} stores)</span></div>
                    <div className="fac-bar-wrap">
                      <div className="fac-bar"
                        style={{
                          width: Math.min(parseFloat(f.avail), 100) + "%",
                          background: f.color || (f.avail >= 90 ? '#27ae60' : f.avail >= 75 ? '#f39c12' : '#e74c3c'),
                          color: "#fff",
                        }}>
                        {f.avail >= 20 ? f.avail + "%" : ""}
                      </div>
                    </div>
                    <div className="fac-pct" style={{ color: f.avail >= 90 ? '#27ae60' : f.avail >= 75 ? '#f39c12' : '#e74c3c' }}>
                      {f.avail}%
                    </div>
                  </div>
                )) : <p style={{ color: '#999' }}>Loading...</p>}
              </div>
              {facilities.length > 0 && (() => {
                const lowFacs = facilities.filter(f => parseFloat(f.avail) < 75);
                return lowFacs.length > 0 ? (
                  <div className="info-box mt-3">
                    ⚠️ {lowFacs.map(f => `${f.name} (${f.avail}%)`).join(', ')} {lowFacs.length === 1 ? 'is a' : 'are'} priority gap{lowFacs.length > 1 ? 's' : ''}.
                  </div>
                ) : (
                  <div className="info-box mt-3" style={{ background: '#dcfce7', color: '#15803d' }}>
                    ✅ All facility types are above 75% availability target.
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* District Availability Table */}
      <div className="row g-3 mt-1">
        <div className="col-12">
          <div className="card overflow-hidden">
            <CardHead
              icon="fas fa-th"
              title="District Wise Stock Availability Summary"
              subtitle={`${dcMatrix.length} districts`}
            >
              <button className="btn-icon">⬇ Export</button>
            </CardHead>
            <div className="card-body p-0 overflow-auto"
              style={{ overflowX: "auto", overflowY: "scroll", height: "400px" }}>
              <table className="data-table" style={{ fontSize: ".75rem" }}>
                <thead>
                  <tr>
                    <th style={{ position: "sticky", left: 0, background: "#f8fafc", zIndex: 2, textAlign: "left" }}>District</th>
                    <th style={{ textAlign: "center" }}>Availability %</th>
                    <th style={{ textAlign: "center" }}>Total Stores</th>
                    <th style={{ textAlign: "center" }}>Stores ≥ 90%</th>
                    <th style={{ textAlign: "center" }}>Stores &lt; 50%</th>
                  </tr>
                </thead>
                <tbody>
                  {dcMatrix.map((r, i) => (
                    <tr key={i}>
                      <td style={{ position: "sticky", left: 0, background: "#fff", zIndex: 1, fontWeight: 600 }}>
                        {r.name}
                      </td>
                      <td style={{ textAlign: "center" }}>{cellBadge(r.avail)}</td>
                      <td style={{ textAlign: "center", fontWeight: 600 }}>{r.total_stores}</td>
                      <td style={{ textAlign: "center" }}>
                        <span style={{ color: '#15803d', fontWeight: 700 }}>{r.stores_above_90}</span>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <span style={{ color: r.stockout_stores > 0 ? '#b91c1c' : '#15803d', fontWeight: 700 }}>
                          {r.stockout_stores}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
